import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  LogOut,
  RefreshCw,
  Mail,
  Phone,
  Building2,
  Calendar,
  Clock,
  Search,
  Loader2,
  Inbox,
} from "lucide-react";

function formatDate(d) {
  try {
    return new Date(d).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return d;
  }
}

export default function AdminDashboard() {
  const { user, logout, authedAxios } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (user === false) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedAxios().get("/contacts");
      setContacts(data);
      if (data.length && !active) setActive(data[0]);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        navigate("/admin", { replace: true });
      } else {
        toast.error("Failed to load contacts.");
      }
    } finally {
      setLoading(false);
    }
  }, [authedAxios, active, logout, navigate]);

  useEffect(() => {
    if (user && user !== false) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const filtered = contacts.filter((c) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      c.name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.company?.toLowerCase().includes(q) ||
      c.message?.toLowerCase().includes(q)
    );
  });

  const handleLogout = () => {
    logout();
    toast.success("Signed out.");
    navigate("/admin", { replace: true });
  };

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#050505] grid place-items-center text-slate-500">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="sticky top-0 z-40 glass-strong border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="relative h-7 w-7 grid place-items-center rounded-full bg-[#007AFF]/20 border border-[#007AFF]/40">
              <span className="h-2 w-2 rounded-full bg-[#00F0FF]" />
            </span>
            <span className="font-display text-base font-extrabold tracking-tight">
              Brand<span className="text-[#00F0FF]">With</span>Abdo
            </span>
            <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] text-[#00F0FF] border border-[#007AFF]/40">
              Admin
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              data-testid="admin-refresh"
              onClick={load}
              className="grid place-items-center h-9 w-9 rounded-full bg-white/[0.04] border border-white/10 hover:border-white/25 transition-colors"
              aria-label="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              data-testid="admin-logout"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.04] border border-white/10 text-sm hover:border-white/25 transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* Title */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#00F0FF]">
              / Inbox
            </div>
            <h1 className="mt-3 font-display text-4xl md:text-5xl font-extrabold tracking-[-0.02em]">
              Leads · {contacts.length}
            </h1>
            <p className="mt-2 text-slate-400 text-sm">
              Signed in as <span className="text-white">{user?.email}</span>
            </p>
          </div>
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, company..."
              className="w-full bg-white/[0.04] border border-white/10 rounded-full pl-10 pr-4 py-2.5 text-sm placeholder:text-slate-500 focus:outline-none focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF] transition-colors"
            />
          </div>
        </div>

        {/* Empty state */}
        {!loading && contacts.length === 0 && (
          <div className="glass rounded-3xl p-16 text-center">
            <Inbox className="h-10 w-10 text-slate-500 mx-auto" />
            <h3 className="mt-4 font-display text-2xl font-bold">No leads yet</h3>
            <p className="mt-2 text-slate-400">
              Submissions to your contact form will appear here.
            </p>
          </div>
        )}

        {/* List + detail */}
        {contacts.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            <div className="lg:col-span-5 glass rounded-3xl p-2 max-h-[78vh] overflow-y-auto">
              {filtered.map((c) => {
                const selected = active?.id === c.id;
                return (
                  <button
                    key={c.id}
                    data-testid={`lead-row-${c.id}`}
                    onClick={() => setActive(c)}
                    className={`w-full text-left p-4 rounded-2xl transition-colors ${
                      selected
                        ? "bg-[#007AFF]/10 border border-[#007AFF]/40"
                        : "border border-transparent hover:bg-white/[0.03]"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{c.name}</div>
                        <div className="text-xs text-slate-400 truncate">
                          {c.company || c.email}
                        </div>
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 shrink-0">
                        {formatDate(c.created_at).split(",")[0]}
                      </div>
                    </div>
                    {c.service && (
                      <div className="mt-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] text-[#00F0FF] border border-[#007AFF]/30">
                        {c.service}
                      </div>
                    )}
                    <p className="mt-2 text-sm text-slate-400 line-clamp-2">
                      {c.message}
                    </p>
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <div className="p-8 text-center text-slate-500 text-sm">
                  No matches for "{query}"
                </div>
              )}
            </div>

            {/* Detail */}
            <div className="lg:col-span-7">
              {active ? (
                <div
                  data-testid="lead-detail"
                  className="glass-strong rounded-3xl p-8 sticky top-24"
                >
                  <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#00F0FF]">
                    / Lead
                  </div>
                  <h2 className="mt-3 font-display text-3xl font-extrabold tracking-[-0.02em]">
                    {active.name}
                  </h2>
                  <p className="mt-1 text-sm text-slate-400">
                    Received {formatDate(active.created_at)}
                  </p>

                  <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      { icon: Mail, label: "Email", value: active.email, href: `mailto:${active.email}` },
                      { icon: Phone, label: "Phone", value: active.phone, href: active.phone ? `tel:${active.phone}` : null },
                      { icon: Building2, label: "Company", value: active.company || "—" },
                      { icon: Calendar, label: "Preferred date", value: active.preferred_date || "—" },
                      { icon: Clock, label: "Preferred time", value: active.preferred_time || "—" },
                    ].map((row) => (
                      <div key={row.label} className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-4">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5">
                          <row.icon className="h-3.5 w-3.5" />
                          {row.label}
                        </div>
                        {row.href ? (
                          <a
                            href={row.href}
                            className="text-white text-sm font-medium hover:text-[#00F0FF] break-all"
                          >
                            {row.value}
                          </a>
                        ) : (
                          <div className="text-white text-sm font-medium break-all">
                            {row.value}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {active.service && (
                    <div className="mt-3 inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.18em] text-[#00F0FF] border border-[#007AFF]/30">
                      {active.service}
                    </div>
                  )}

                  <div className="mt-6">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
                      Message
                    </div>
                    <div className="bg-[#050505] border border-white/[0.06] rounded-2xl p-5 text-slate-200 text-sm leading-relaxed whitespace-pre-wrap">
                      {active.message}
                    </div>
                  </div>

                  <div className="mt-7 flex flex-wrap gap-3">
                    <a
                      href={`mailto:${active.email}?subject=Re%3A%20your%20enquiry%20%E2%80%94%20BrandWithAbdo`}
                      data-testid="lead-reply"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#007AFF] text-white font-semibold hover:bg-[#00F0FF] hover:text-black transition-colors"
                    >
                      <Mail className="h-4 w-4" /> Reply by email
                    </a>
                    {active.phone && (
                      <a
                        href={`tel:${active.phone}`}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-white hover:bg-white/[0.04] transition-colors"
                      >
                        <Phone className="h-4 w-4" /> Call
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <div className="glass rounded-3xl p-12 text-center text-slate-500">
                  Select a lead to view details
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
